import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ContainerDashboard from "../../components/layouts/ContainerDashboard";
import Loader from "../Loader";
import swal from "sweetalert";
import { axiosInstance } from "../Config";
import { useNavigate } from "react-router-dom";
import Pagination, { PerPageSelect } from "../../components/Pagination";
import { usePermissionData } from "../../AppContext";

const MAX_VIDEO_SIZE = 300 * 1024 * 1024; // 300 MB

const emptyForm = {
  name: "",
  category: "",
  description: "",
  tags: "",
  videoFile: null,
  videoType: "",
  createdBy: "",
};

const parseTags = (tagsStr) =>
  tagsStr
    .split(",")
    .map((t) => t.trim().replace(/^#/, ""))
    .filter(Boolean);

const normalizeTags = (tags) => {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags;
  return tags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
};

const VideosIndex = () => {
  const navigate = useNavigate();
  const { checkAccess } = usePermissionData();

  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [creators, setCreators] = useState([]);
  const [player, setPlayer] = useState(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const isFirstRender = useRef(true);

  useEffect(() => {
    document.title = "Videos | Mindora";
    window.scrollTo(0, 0);
    fetchVideos();
    fetchCategories();
    fetchCreators();
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPage(1);
    const timer = setTimeout(() => fetchVideos(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchVideos = async (search = "") => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await axiosInstance.get("videos", { params });
      const list = (res.data?.data ?? res.data ?? []).map((v) => ({
        ...v,
        name: v.videoName || v.name || "",
        tags: normalizeTags(v.tags),
        category:
          typeof v.category === "object"
            ? v.category?.categoryName
            : v.category,
        categoryId:
          typeof v.category === "object" ? v.category?.id : v.categoryId,
      }));
      setVideos(list);
    } catch {
      swal("", "Failed to load videos.", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axiosInstance.get("categories");
      setCategories(res.data?.data ?? res.data ?? []);
    } catch {
      console.error("Failed to load categories");
    }
  };

  const fetchCreators = async () => {
    try {
      const res = await axiosInstance.get("creators");
      const data = res.data?.data ?? res.data ?? [];
      setCreators(Array.isArray(data) ? data : []);
    } catch {
      console.error("Failed to load creators");
    }
  };

  const onSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const openAddModal = () => {
    setEditingVideo(null);
    setFormData(emptyForm);
    setFormErrors({});
    setShowModal(true);
  };

  const openEditModal = async (video) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`videos/${video.id}`);
      const v = res.data?.data ?? res.data;
      setEditingVideo(v);
      setFormData({
        name: v.videoName || v.name || "",
        category:
          v.categoryName ??
          (typeof v.category === "object"
            ? v.category?.categoryName
            : v.category) ??
          "",
        description: v.description || "",
        tags: normalizeTags(v.tags).join(", "),
        videoFile: null,
        videoType: v.videoType || "",
        createdBy:
          typeof v.createdBy === "object"
            ? (v.createdBy?.id ?? "")
            : (v.createdBy ?? ""),
      });
      setFormErrors({});
      setShowModal(true);
    } catch {
      swal("", "Failed to load video details.", "error");
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingVideo(null);
    setFormData(emptyForm);
    setFormErrors({});
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = "Video name is required.";
    if (!formData.category) errors.category = "Category is required.";
    if (!editingVideo && !formData.videoFile)
      errors.videoFile = "Please select a video file.";
    else if (formData.videoFile && formData.videoFile.size > MAX_VIDEO_SIZE)
      errors.videoFile = "Video file size must not exceed 300 MB.";
    return errors;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setLoading(true);
    const tags = parseTags(formData.tags);

    const fd = new FormData();
    const selectedCat = categories.find(
      (c) => c.categoryName === formData.category,
    );
    fd.append("videoName", formData.name);
    fd.append("categoryId", selectedCat?.id ?? formData.category);
    fd.append("description", formData.description);
    fd.append("tags", tags.join(","));
    fd.append("videoType", formData.videoType);
    if (formData.createdBy) fd.append("createdBy", formData.createdBy);
    if (formData.videoFile) fd.append("videoFile", formData.videoFile);

    try {
      if (editingVideo) {
        await axiosInstance.put(`videos/${editingVideo.id}`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        swal("", "Video updated successfully!", "success");
      } else {
        await axiosInstance.post("videos", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        swal("", "Video uploaded successfully!", "success");
      }
      closeModal();
      fetchVideos(searchQuery);
    } catch (err) {
      swal(
        "",
        err?.response?.data?.message || "Something went wrong!",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(videos.length / itemsPerPage);
  const pagedVideos = videos.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const onDelete = (videoId) => {
    swal({
      title: "Are you sure?",
      text: "You want to delete this video?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        setLoading(true);
        try {
          await axiosInstance.delete(`videos/${videoId}`);
          swal("", "Video deleted successfully!", "success");
          setVideos((prev) => prev.filter((v) => v.id !== videoId));
        } catch {
          swal("", "Something went wrong!", "error");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  return (
    <>
      {loading && <Loader />}
      <ContainerDashboard
        title="Videos"
        description="Manage your video content"
      >
        <section className="ps-dashboards">
          <div
            className="p-4 py-5 bg-gradientt rounded-4"
            style={{ minHeight: "75vh" }}
          >
            <div className="d-md-flex justify-content-between align-items-center mb-4 gap-3">
              <div className="d-flex align-items-center">
                <div className="ps-form--search-simple">
                  <input
                    className="form-control bg-white"
                    type="text"
                    value={searchQuery}
                    onChange={onSearch}
                    placeholder="Search by name, category or tag..."
                  />
                  <button type="button">
                    <i className="icon icon-magnifier"></i>
                  </button>
                </div>
                <PerPageSelect itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} setPage={setPage} />
              </div>
              <div className="d-flex gap-2 flex-shrink-0 mt-3 mt-md-0">
                {checkAccess(2, 2) && (
                  <button
                    type="button"
                    className="button btn-green color-white"
                    onClick={openAddModal}
                  >
                    <i className="icon-cloud-upload me-2 fs-5"></i>Upload Video
                  </button>
                )}
                <button
                  type="button"
                  className="button"
                  style={{
                    background: "#1a1a2e",
                    color: "#fff",
                    borderRadius: "12px",
                  }}
                  onClick={() => navigate(-1)}
                >
                  <i className="icon-arrow-left me-2"></i>Back
                </button>
              </div>
            </div>

            {videos.length === 0 ? (
              <div className="text-center py-5" style={{ color: "#aaa" }}>
                <i
                  className="icon-film"
                  style={{ fontSize: 52, opacity: 0.3 }}
                ></i>
                <p className="mt-3 mb-0">No videos found.</p>
              </div>
            ) : (
              <>
                <div className="row g-4">
                  {pagedVideos.map((video) => (
                    <div
                      key={video.id}
                      className="col-xl-3 col-lg-4 col-md-6 col-12"
                    >
                      <VideoCard
                        video={video}
                        onEdit={() => openEditModal(video)}
                        onDelete={() => onDelete(video.id)}
                        onPlay={() => setPlayer(video)}
                      />
                    </div>
                  ))}
                </div>
                <Pagination
                  page={page}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalItems={videos.length}
                  showingCount={pagedVideos.length}
                />
              </>
            )}
          </div>
        </section>
      </ContainerDashboard>

      {showModal && (
        <VideoModal
          isEdit={!!editingVideo}
          formData={formData}
          setFormData={setFormData}
          formErrors={formErrors}
          onSubmit={onSubmit}
          onClose={closeModal}
          categories={categories}
          creators={creators}
        />
      )}

      {player &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0,0,0,0.92)",
              zIndex: 2147483647,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
            onClick={() => setPlayer(null)}
          >
            <button
              onClick={() => setPlayer(null)}
              style={{
                position: "absolute",
                top: 20,
                right: 24,
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: "50%",
                width: 40,
                height: 40,
                color: "#fff",
                fontSize: 20,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ✕
            </button>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                width: "82vw",
                maxWidth: 960,
              }}
            >
              <div
                style={{
                  width: "100%",
                  borderRadius: 12,
                  overflow: "hidden",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
                  background: "#000",
                }}
              >
                <video
                  key={player.id}
                  src={player.videoUrl}
                  controls
                  autoPlay
                  style={{ width: "100%", height: "480px", display: "block" }}
                />
              </div>
              <p
                className="mb-0 fw-semibold"
                style={{ color: "#fff", fontSize: 15 }}
              >
                {player.name}
              </p>
              <p
                className="mb-0"
                style={{ color: "rgba(255,255,255,0.55)", fontSize: 12 }}
              >
                <span
                  className="badge rounded-pill px-3 py-1 me-2"
                  style={{
                    background: "rgba(164,76,215,0.3)",
                    color: "#e0c8ff",
                  }}
                >
                  {player.category}
                </span>
                <i className="bi bi-calendar3 me-1"></i>
                {player.uploadedAt || player.createdAt}
              </p>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

const tagPillStyle = {
  fontSize: 11,
  fontWeight: 600,
  background: "rgba(164,76,215,0.08)",
  color: "#A44CD7",
  borderRadius: 20,
  padding: "2px 8px",
  flexShrink: 0,
  whiteSpace: "nowrap",
};

const VideoCard = ({ video, onEdit, onDelete, onPlay }) => {
  const { checkAccess } = usePermissionData();
  const tags = video.tags || [];
  const desc = video.description || "";
  const shortDesc = desc.length > 75 ? desc.slice(0, 75).trimEnd() + "…" : desc;

  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(tags.length);

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el || tags.length === 0) {
      setVisibleCount(tags.length);
      return;
    }
    const containerRight = el.getBoundingClientRect().right;
    const kids = [...el.children];
    let count = 0;
    for (const kid of kids) {
      if (kid.getBoundingClientRect().right <= containerRight + 1) count++;
      else break;
    }
    setVisibleCount(Math.max(count, 1));
  }, [tags]);

  const visibleTags = tags.slice(0, visibleCount);
  const extraCount = tags.length - visibleCount;

  return (
    <div
      className="rounded-4 overflow-hidden h-100 d-flex flex-column"
      style={{
        background: "#fff",
        boxShadow: "0 2px 16px rgba(164,76,215,0.10)",
        border: "1px solid rgba(164,76,215,0.10)",
      }}
    >
      <div
        className="position-relative"
        style={{
          height: 160,
          background: "linear-gradient(135deg, #fdf0ff, #e5fafc)",
          cursor: "pointer",
          overflow: "hidden",
        }}
        onClick={onPlay}
      >
        {video.videoUrl ? (
          <video
            src={video.videoUrl}
            preload="metadata"
            muted
            playsInline
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onLoadedMetadata={(e) => {
              e.target.currentTime = 1;
            }}
          />
        ) : (
          <div className="d-flex align-items-center justify-content-center h-100">
            <i
              className="bi bi-play-circle-fill"
              style={{ fontSize: 52, color: "var(--primary)", opacity: 0.35 }}
            ></i>
          </div>
        )}
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: 50,
              height: 50,
              borderRadius: "50%",
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
            }}
          >
            <i
              className="bi bi-play-fill"
              style={{ fontSize: 22, color: "#fff", marginLeft: 3 }}
            ></i>
          </div>
        </div>
        <span
          className="position-absolute top-0 end-0 m-2 badge rounded-pill px-2 py-1"
          style={{ background: "var(--btn-primary)", fontSize: "11px" }}
        >
          {video.category}
        </span>
        {/* {video.videoType && (
          <span
            className="position-absolute top-0 start-0 m-2 badge rounded-pill px-2 py-1"
            style={{
              background: video.videoType === "short" ? "#0CACC2" : "#A44CD7",
              fontSize: "11px",
            }}
          >
            {video.videoType}
          </span>
        )} */}
      </div>

      <div className="d-flex justify-content-between flex-column p-3 flex-grow-1">
        <div>
          <h6
            className="fw-semibold mb-1 text-truncate"
            title={video.name}
            style={{ color: "#1a1a2e" }}
          >
            {video.name}
          </h6>

          {shortDesc && (
            <p
              className="mb-2"
              style={{ fontSize: 12, color: "#888", lineHeight: 1.5 }}
              title={desc}
            >
              {shortDesc}
            </p>
          )}
        </div>

        <div>
          {tags.length > 0 && (
            <div className="mb-2 position-relative">
              <div
                ref={measureRef}
                aria-hidden="true"
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  gap: 4,
                  visibility: "hidden",
                  position: "absolute",
                  width: "100%",
                  pointerEvents: "none",
                }}
              >
                {tags.map((tag, i) => (
                  <span key={i} style={tagPillStyle}>
                    #{tag}
                  </span>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                  overflow: "hidden",
                  gap: 4,
                }}
              >
                {visibleTags.map((tag, i) => (
                  <span key={i} style={tagPillStyle}>
                    #{tag}
                  </span>
                ))}
              </div>
              {extraCount > 0 && (
                <div className="mt-1">
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      background: "rgba(12,172,194,0.08)",
                      color: "#0CACC2",
                      borderRadius: 20,
                      padding: "2px 8px",
                    }}
                  >
                    +{extraCount} more
                  </span>
                </div>
              )}
            </div>
          )}

          <p className="text-muted mb-3" style={{ fontSize: "12px" }}>
            <i className="bi bi-calendar3 me-1"></i>Uploaded:{" "}
            {new Date(video.uploadedAt || video.createdAt).toLocaleDateString(
              "en-GB",
            )}
          </p>

          <div className="mt-auto d-flex gap-2">
            {checkAccess(2, 3) && (
              <button
                type="button"
                className="btn flex-fill fw-semibold"
                style={{
                  background: "var(--primary)",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "5px 14px",
                  fontSize: "13px",
                }}
                onClick={onEdit}
              >
                <i className="icon-pencil me-1"></i> Edit
              </button>
            )}
            {checkAccess(2, 4) && (
              <button
                type="button"
                className="btn flex-fill fw-semibold"
                style={{
                  background: "#EC2434",
                  color: "#fff",
                  borderRadius: "8px",
                  padding: "5px 14px",
                  fontSize: "13px",
                }}
                onClick={onDelete}
              >
                <i className="icon-trash me-1"></i> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const VideoModal = ({
  isEdit,
  formData,
  setFormData,
  formErrors,
  onSubmit,
  onClose,
  categories,
  creators,
}) => {
  const videoInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "videoFile") {
      setFormData((prev) => ({ ...prev, videoFile: files[0] || null }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        zIndex: 99999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "70%",
          animation: "videoModalIn 0.22s ease",
          maxHeight: "92vh",
          overflowY: "auto",
        }}
      >
        <style>{`
                    @keyframes videoModalIn {
                        from { transform: translateY(-18px); opacity: 0; }
                        to   { transform: translateY(0);     opacity: 1; }
                    }
                `}</style>

        <div
          className="rounded-4 p-4"
          style={{
            background: "#fff",
            boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 fw-bold" style={{ color: "var(--primary)" }}>
              {isEdit ? "Edit Video" : "Upload Video"}
            </h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <form onSubmit={onSubmit} noValidate>
            <div className="row mb-3">
              <div className="col-4">
                <label className="form-label fw-semibold">
                  Category <span className="text-danger">*</span>
                </label>
                <select
                  className={`form-select ${
                    formErrors.category ? "is-invalid" : ""
                  }`}
                  style={{
                    height: "50px",
                    fontSize: "14px",
                    paddingLeft: "20px",
                  }}
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.categoryName}>
                      {cat.categoryName}
                    </option>
                  ))}
                </select>
                {formErrors.category && (
                  <div className="invalid-feedback">{formErrors.category}</div>
                )}
              </div>
              <div className="col-4">
                <label className="form-label fw-semibold">
                  Video Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className={`form-control ${
                    formErrors.name ? "is-invalid" : ""
                  }`}
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter video name"
                />
                {formErrors.name && (
                  <div className="invalid-feedback">{formErrors.name}</div>
                )}
              </div>
              <div className="col-4">
                <label className="form-label fw-semibold">Video Type</label>
                <select
                  className="form-select"
                  style={{
                    height: "50px",
                    fontSize: "14px",
                    paddingLeft: "20px",
                  }}
                  name="videoType"
                  value={formData.videoType}
                  onChange={handleChange}
                >
                  <option value="">Select type</option>
                  <option value="long">Long Video</option>
                  <option value="short">Short Video</option>
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <label className="form-label fw-semibold">Created By</label>
                <select
                  className="form-select"
                  style={{
                    height: "50px",
                    fontSize: "14px",
                    paddingLeft: "20px",
                  }}
                  name="createdBy"
                  value={formData.createdBy}
                  onChange={handleChange}
                >
                  <option value="">Select creator</option>
                  {creators.map((creator) => (
                    <option key={creator.id} value={creator.id}>
                      {creator.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="row mb-3">
              <div className="col-6">
                <label className="form-label fw-semibold">Tags</label>
                <input
                  type="text"
                  className="form-control"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="#colors, #learning, #kids"
                />
                <small className="text-muted mt-1 d-block">
                  Comma-separated. # is optional.
                </small>
              </div>
              <div className="col-6">
                <label className="form-label fw-semibold">
                  Video File (Max 300 MB){" "}
                  {!isEdit ? (
                    <span className="text-danger">*</span>
                  ) : (
                    <span className="text-muted fw-normal ms-1 small">
                      (leave blank to keep existing)
                    </span>
                  )}
                </label>
                <input
                  type="file"
                  className={`form-control ${
                    formErrors.videoFile ? "is-invalid" : ""
                  }`}
                  name="videoFile"
                  accept="video/*"
                  ref={videoInputRef}
                  onChange={handleChange}
                  style={{ paddingTop: "10px" }}
                />
                {formData.videoFile && (
                  <small className="text-muted mt-1 d-block">
                    Selected: {formData.videoFile.name}
                  </small>
                )}
                {formErrors.videoFile && (
                  <div className="invalid-feedback">{formErrors.videoFile}</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Brief description of the video..."
                rows={3}
                style={{ resize: "none", fontSize: 14 }}
              />
            </div>

            <div className="d-flex justify-content-end gap-3">
              <button
                type="button"
                className="btn btn-light px-4"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn px-4 text-white fw-semibold"
                style={{
                  background:
                    "linear-gradient(135deg, var(--btn-primary), var(--btn-secondary))",
                  border: "none",
                }}
              >
                {isEdit ? "Save Changes" : "Upload"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VideosIndex;
